import React, { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import {
  type LutValues,
  positionLutValueList,
  type PositionLutValues,
  orgTypeServerCall,
  type OrgTypesLutValues,
  type CountriesLutValues,
  countryLutServerCall,
  getOrgUrlDescTypes,
  type UrlDescTypeLutValues,
  getPrefixes,
  getUsPrefixes,
  type PrefixLutValues,
  getRegions,
  type RegionLutValues,
  getRoles,
  type UsRoleLutValues,
  legroleLutValueList,
  legseatstatusLutValueList,
  usPoliticalPartiesLutValueList,
  type LegRoleValues,
  type LegSeatStatusValues,
  type PoliticalPartyValues,
  usCountryLutServerCall,
  type LegDistrictValues,
  usLegDistrictsLutValueList,
  type CustomBehaviourValues,
  customBehaviourLutValueList,
  roleResponsibilityLutValueList,
  type RoleResponsibilityValues,
  type UsUrlDescTypeLutValues,
  roleUrlDescLutValueList,
  roleAddressTypeLutValueList,
  type RoleAddressTypeLutValues,
  buildingLutValueList,
  type BuildingLutValues,
  getExecutiveOrgParentsLut,
  type ExecOrgParentLutValues,
  getExecRolesLut,
  type ExecRoleLutValues,
  getProtectedRoleHolders,
  type ProtectedRoleHoldersLutValues,
  getExecPositionTypesLut,
  type ExecPosTypeLutValues,
  getUsStatesLut,
  type UsStateLutValues
} from 'services'

const createLutContext = (): React.Context<any> => createContext(undefined)

export const LUT_TTL = {
  DEFAULT: 15 * 60 * 1000,
  LONG: 3 * 24 * 60 * 60 * 1000,
  STATIC: 365 * 24 * 60 * 60 * 1000
}

interface CreateLutProviderArgs<T extends LutValues> {
  initialValue: T
  endpoint: () => Promise<T>
  expirationTime: number
  localStorageKey: string
}

interface LutProviderProps {
  children: ReactNode
}

export class SerializableMap<K, V> extends Map<K, V> {
  toJSON (): Array<[K, V]> {
    return Array.from(this.entries())
  }
}

interface UseLutResult<T extends LutValues> {
  lutValues: T
  loading: boolean
  setLutValues: (values: T) => void
}

interface LutProviderResult<T extends LutValues> {
  LutProvider: React.FC<LutProviderProps>
  useLuts: () => UseLutResult<T>
}

const hasMapPropertyInNeedOfConversion = (value: any, propertyName: string, expectsMap: boolean): boolean => {
  const hasMapAlready = value[propertyName] instanceof SerializableMap
  if (hasMapAlready) {
    return false
  }
  return expectsMap && Array.isArray(value[propertyName])
}

const lutJSONToObjectWithMap = (json: string, propertyName: string): any => {
  const parsedObject = JSON.parse(json)
  parsedObject[propertyName] = new SerializableMap<number, any>(parsedObject[propertyName])
  return parsedObject
}

const createLutProvider = <T extends LutValues>({ initialValue, endpoint, expirationTime, localStorageKey }: CreateLutProviderArgs<T>): LutProviderResult<T> => {
  const LutContext = createLutContext()

  const LutProvider: React.FC<LutProviderProps> = ({ children }) => {
    const storedLutValues = localStorage.getItem(localStorageKey)
    const propertyName = Object.keys(initialValue)[0]
    const expectsMap = (initialValue as any)[propertyName] instanceof SerializableMap
    const initialLutValues = storedLutValues != null
      ? hasMapPropertyInNeedOfConversion(JSON.parse(storedLutValues), propertyName, expectsMap)
        ? lutJSONToObjectWithMap(storedLutValues, propertyName)
        : JSON.parse(storedLutValues)
      : initialValue
    const [lutValues, setLutValues] = useState<LutValues>(initialLutValues)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      const fetchAndUpdateLutValues = async (): Promise<void> => {
        try {
          const newValues = await endpoint()
          setLutValues(newValues)
        } finally {
          setLoading(false)
        }
      }

      const maximumAcceptedAge = new Date().valueOf() - expirationTime
      if (lutValues === null || lutValues.currentTime == null || new Date(lutValues.currentTime + 'Z').valueOf() < maximumAcceptedAge) {
        setLoading(true)
        fetchAndUpdateLutValues().finally(() => { setLoading(false) })
      } else {
        setLoading(false)
      }

      localStorage.setItem(localStorageKey, JSON.stringify(lutValues))
    }, [lutValues, endpoint, expirationTime, localStorageKey])

    const value = useMemo(() => ({ lutValues, loading, setLutValues }), [lutValues, loading])

    return (
        <LutContext.Provider value={value}>
          {children}
        </LutContext.Provider>
    )
  }

  const useLuts = (): UseLutResult<T> => {
    const context = useContext(LutContext)

    if (context == null) {
      throw new Error('useLuts must be used within a LutProvider')
    }

    return context
  }

  return { LutProvider, useLuts }
}

const { LutProvider: PositionLutProvider, useLuts: usePositionLuts } = createLutProvider<PositionLutValues>({
  initialValue: { positionTypes: [] },
  endpoint: positionLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'positionLutValues'
})

const { LutProvider: OrganizationTypeLutProvider, useLuts: useOrganizationTypeLuts } = createLutProvider<OrgTypesLutValues>({
  initialValue: { organizationTypes: [] },
  endpoint: orgTypeServerCall,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'organizationTypeLutValues'
})

const { LutProvider: CountriesLutProvider, useLuts: useCountriesLuts } = createLutProvider<CountriesLutValues>({
  initialValue: { countries: [] },
  endpoint: countryLutServerCall,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'countriesLutValues'
})

const { LutProvider: UsCountriesLutProvider, useLuts: useUsCountriesLuts } = createLutProvider<CountriesLutValues>({
  initialValue: { countries: [] },
  endpoint: usCountryLutServerCall,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'usCountriesLutValues'
})

const { LutProvider: UrlDescTypeProvider, useLuts: useUrlDescTypeLuts } = createLutProvider<UrlDescTypeLutValues>({
  initialValue: { descTypes: [] },
  endpoint: getOrgUrlDescTypes,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'urlDescTypeLutValues'
})

const { LutProvider: RegionProvider, useLuts: useRegionLuts } = createLutProvider<RegionLutValues>({
  initialValue: { regions: [] },
  endpoint: getRegions,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'regionLutValues'
})

const { LutProvider: UsRoleTypesProvider, useLuts: useUsRoleTypeLuts } = createLutProvider<UsRoleLutValues>({
  initialValue: { roles: new SerializableMap() },
  endpoint: getRoles,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsRoleTypes'
})

const { LutProvider: UsLegRoleTypesProvider, useLuts: useUsLegRoleTypeLuts } = createLutProvider<LegRoleValues>({
  initialValue: { legRoles: [] },
  endpoint: legroleLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsLegRoleTypes'
})

const { LutProvider: LegSeatStatusProvider, useLuts: useSeatStatusLuts } = createLutProvider<LegSeatStatusValues>({
  initialValue: { statuses: [] },
  endpoint: legseatstatusLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsLegSeatStatusTypes'
})

const { LutProvider: LegPoliticalPartiesProvider, useLuts: useLegPoliticalPartiesLuts } = createLutProvider<PoliticalPartyValues>({
  initialValue: { parties: [] },
  endpoint: usPoliticalPartiesLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsPoliticalParties'
})

export const US_PREFIX_LUT_STORAGE_KEY = 'UsPrefixes'
export const PREFIX_LUT_STORAGE_KEY = 'prefixLutValues'

const { LutProvider: UsPrefixProvider, useLuts: useUsPrefixLuts } = createLutProvider<PrefixLutValues>({
  initialValue: { prefixes: [] },
  endpoint: getUsPrefixes,
  expirationTime: LUT_TTL.LONG,
  localStorageKey: US_PREFIX_LUT_STORAGE_KEY
})

const { LutProvider: LegDistrictsProvider, useLuts: useLegDistrictsLuts } = createLutProvider<LegDistrictValues>({
  initialValue: { districts: new SerializableMap() },
  endpoint: usLegDistrictsLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsLegDistricts'
})

const { LutProvider: CustomBehaviourProvider, useLuts: useCustomBehaviourLuts } = createLutProvider<CustomBehaviourValues>({
  initialValue: { custom_behaviours: new SerializableMap() },
  endpoint: customBehaviourLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'CustomBehaviours'
})

const { LutProvider: RoleResponsibilityProvider, useLuts: useRoleResponsibilityLuts } = createLutProvider<RoleResponsibilityValues>({
  initialValue: { responsibilities: new SerializableMap() },
  endpoint: roleResponsibilityLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'RoleResponsibility'
})

const { LutProvider: UsUrlDescTypeProvider, useLuts: useUsUrlDescTypeLuts } = createLutProvider<UsUrlDescTypeLutValues>({
  initialValue: { descTypes: new SerializableMap() },
  endpoint: roleUrlDescLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'urlUsRoleDescTypeLutValues.v2'
})

const { LutProvider: RoleAddressTypeProvider, useLuts: useRoleAddressTypeLuts } = createLutProvider<RoleAddressTypeLutValues>({
  initialValue: { address_types: {} },
  endpoint: roleAddressTypeLutValueList,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'roleAddressTypeLutValues'
})

const { LutProvider: PrefixProvider, useLuts: usePrefixLuts } = createLutProvider<PrefixLutValues>({
  initialValue: { prefixes: [] },
  endpoint: getPrefixes,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: PREFIX_LUT_STORAGE_KEY
})

const { LutProvider: BuildingProvider, useLuts: useBuildingLuts } = createLutProvider<BuildingLutValues>({
  initialValue: { buildings: [] },
  endpoint: buildingLutValueList,
  expirationTime: 480 * 60 * 1000,
  localStorageKey: 'buildingLutValues'
})

const { LutProvider: ExecOrgParentLutProvider, useLuts: useExecOrgParentLuts } = createLutProvider<ExecOrgParentLutValues>({
  initialValue: { parents: new SerializableMap() },
  endpoint: getExecutiveOrgParentsLut,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsExecOrgParents'
})

const { LutProvider: ExecRoleLutProvider, useLuts: useExecRoleLuts } = createLutProvider<ExecRoleLutValues>({
  initialValue: { execRoles: [] },
  endpoint: getExecRolesLut,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'UsExecRoleTypes'
})

const { LutProvider: ProtectedRoleHoldersLutProvider, useLuts: useProtectedRoleHoldersLut } = createLutProvider<ProtectedRoleHoldersLutValues>({
  initialValue: { holders: [] },
  endpoint: getProtectedRoleHolders,
  expirationTime: 15 * 60 * 1000,
  localStorageKey: 'ProtectedRoleHolders'
})

const { LutProvider: ExecPosTypeLutProvider, useLuts: useExecPositionTypeLuts } = createLutProvider<ExecPosTypeLutValues>({
  initialValue: { execPosTypes: [] },
  endpoint: getExecPositionTypesLut,
  expirationTime: 24 * 60 * 60 * 1000,
  localStorageKey: 'UsExecPosTypes'
})

const { LutProvider: UsStateLutProvider, useLuts: useUsStateLuts } = createLutProvider<UsStateLutValues>({
  initialValue: { states: new SerializableMap() },
  endpoint: getUsStatesLut,
  expirationTime: LUT_TTL.STATIC,
  localStorageKey: 'UsStateLutValues.v1'
})

export {
  PositionLutProvider,
  usePositionLuts,
  OrganizationTypeLutProvider,
  useOrganizationTypeLuts,
  CountriesLutProvider,
  useCountriesLuts,
  UsCountriesLutProvider,
  useUsCountriesLuts,
  UrlDescTypeProvider,
  useUrlDescTypeLuts,
  PrefixProvider,
  usePrefixLuts,
  RegionProvider,
  useRegionLuts,
  UsRoleTypesProvider,
  useUsRoleTypeLuts,
  UsLegRoleTypesProvider,
  useUsLegRoleTypeLuts,
  LegSeatStatusProvider,
  useSeatStatusLuts,
  LegPoliticalPartiesProvider,
  useLegPoliticalPartiesLuts,
  UsPrefixProvider,
  useUsPrefixLuts,
  LegDistrictsProvider,
  useLegDistrictsLuts,
  CustomBehaviourProvider,
  useCustomBehaviourLuts,
  RoleResponsibilityProvider,
  useRoleResponsibilityLuts,
  UsUrlDescTypeProvider,
  useUsUrlDescTypeLuts,
  RoleAddressTypeProvider,
  useRoleAddressTypeLuts,
  BuildingProvider,
  useBuildingLuts,
  ExecOrgParentLutProvider,
  useExecOrgParentLuts,
  ExecRoleLutProvider,
  useExecRoleLuts,
  ProtectedRoleHoldersLutProvider,
  useProtectedRoleHoldersLut,
  ExecPosTypeLutProvider,
  useExecPositionTypeLuts,
  UsStateLutProvider,
  useUsStateLuts
}
